import i18n from "@/i18n";

/**
 * ISO 3166-1 alpha-2 codes offered in the billing country selector.
 *
 * Only the codes live here — names are resolved through `Intl.DisplayNames` so
 * the list reads in the user's own language without us shipping seven
 * translations of every country name.
 */
const COUNTRY_CODES = [
  "AD", "AE", "AG", "AL", "AM", "AO", "AR", "AT", "AU", "AZ",
  "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI", "BJ", "BN",
  "BO", "BR", "BS", "BT", "BW", "BY", "BZ", "CA", "CD", "CF",
  "CG", "CH", "CI", "CL", "CM", "CN", "CO", "CR", "CV", "CY",
  "CZ", "DE", "DJ", "DK", "DM", "DO", "DZ", "EC", "EE", "EG",
  "ER", "ES", "ET", "FI", "FJ", "FM", "FR", "GA", "GB", "GD",
  "GE", "GH", "GM", "GN", "GQ", "GR", "GT", "GW", "GY", "HK",
  "HN", "HR", "HT", "HU", "ID", "IE", "IL", "IN", "IS", "IT",
  "JM", "JO", "JP", "KE", "KG", "KH", "KI", "KM", "KN", "KR",
  "KW", "KZ", "LA", "LB", "LC", "LI", "LK", "LR", "LS", "LT",
  "LU", "LV", "MA", "MC", "MD", "ME", "MG", "MH", "MK", "ML",
  "MM", "MN", "MR", "MT", "MU", "MV", "MW", "MX", "MY", "MZ",
  "NA", "NE", "NG", "NI", "NL", "NO", "NP", "NR", "NZ", "OM",
  "PA", "PE", "PG", "PH", "PK", "PL", "PT", "PW", "PY", "QA",
  "RO", "RS", "RW", "SA", "SB", "SC", "SE", "SG", "SI", "SK",
  "SL", "SM", "SN", "SR", "ST", "SV", "SZ", "TD", "TG", "TH",
  "TJ", "TL", "TM", "TN", "TO", "TR", "TT", "TV", "TW", "TZ",
  "UA", "UG", "US", "UY", "UZ", "VC", "VE", "VN", "VU", "WS",
  "ZA", "ZM", "ZW",
] as const;

export type CountryOption = { code: string; name: string };

/** Localized country name, falling back to the raw code on older runtimes. */
function countryName(code: string, displayNames?: Intl.DisplayNames): string {
  try {
    return displayNames?.of(code) ?? code;
  } catch {
    return code;
  }
}

/** Country options sorted by their localized name for the billing selector. */
export function getCountryOptions(locale?: string): CountryOption[] {
  const resolved = locale ?? i18n.resolvedLanguage ?? "en";

  let displayNames: Intl.DisplayNames | undefined;
  try {
    displayNames = new Intl.DisplayNames([resolved], { type: "region" });
  } catch {
    displayNames = undefined;
  }

  return COUNTRY_CODES.map((code) => ({
    code,
    name: countryName(code, displayNames),
  })).sort((a, b) => a.name.localeCompare(b.name, resolved));
}

/**
 * Best guess at the user's billing country from their locale (e.g. "en-GB" →
 * "GB"). Only a default for the selector — the user can always change it, and
 * the server validates whatever comes back.
 */
export function countryForLocale(locale?: string): string {
  const resolved =
    locale ??
    (typeof Intl !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().locale
      : "en-US");

  const region = resolved
    .toUpperCase()
    .replace("_", "-")
    .split("-")
    .slice(1)
    .find((part) => /^[A-Z]{2}$/.test(part));

  return region && COUNTRY_CODES.includes(region as (typeof COUNTRY_CODES)[number])
    ? region
    : "US";
}
